import type {
  AmbientNarrativeContest,
  AmbientNarrativeContestResolution,
  AmbientRecoveredNote,
  QRAnswerItem,
} from '@scripsteam/scrips-signal-ds';
import { NARRATIVE_SLOT_LABEL, type NarrativeCollision } from './to-narrative-answers';
import { landedLine, readSink, type OrphanEntry } from './orphan-sink';

/**
 * What ambient produced that did NOT become a proposal card, and what the clinician did about it
 * (PROD-2445, PROD-2447). Pure: the host holds one of these in state and the functions below are the
 * only way it changes, so every transition is testable without rendering an encounter.
 *
 * TWO KINDS, both "never silent":
 *
 *   - NARRATIVE CONTESTS — a dictated narrative that lost to an answer already in its slot. The
 *     answer is never overwritten automatically; the pair is shown and the clinician chooses.
 *   - RECOVERED NOTES — a finding Contract E already appended to the encounter narrative with no
 *     click. Shown after the fact, under Accepted, with Undo. `written` only once a save that carried
 *     the line has resolved.
 *
 * Session-scoped, like every other decision in the ambient panel.
 */
export interface AmbientRecoveries {
  /** The latest collision per slot linkId. A re-analysis with new wording replaces the old one. */
  contests: ReadonlyMap<string, NarrativeCollision>;
  /** Choices, by collision key — so the same wording is never asked twice. */
  resolutions: ReadonlyMap<string, AmbientNarrativeContestResolution>;
  /** Every line this session landed in the sink, in landing order. */
  notes: readonly TrackedNote[];
}

export interface TrackedNote {
  id: string;
  text: string;
  reason: OrphanEntry['reason'];
  status: AmbientRecoveredNote['status'];
}

export const EMPTY_RECOVERIES: AmbientRecoveries = {
  contests: new Map(),
  resolutions: new Map(),
  notes: [],
};

/** Why a line had nowhere else to go, in the clinician's words — never invented, one per reason. */
export const ORPHAN_REASON_LABEL: Readonly<Record<OrphanEntry['reason'], string>> = {
  unclassified: 'Not a kind of finding any section of this template accepts',
  'no-template-item': 'This template has no section for it',
  'measured-finding': 'Measured, with no template section to hold it',
  'withheld-not-a-diagnosis': 'Withheld from the diagnosis list',
  'dictated-vital': 'A vital heard in the room, not a confirmed reading',
  'pair-not-carried': 'A measured value the note did not carry',
  'transcript-gap': 'Audio that was not captured when the connection dropped',
};

/** Record the collisions one Soap frame produced. Returns `state` itself when nothing changed. */
export function withCollisions(state: AmbientRecoveries, collisions: readonly NarrativeCollision[]): AmbientRecoveries {
  if (collisions.length === 0) return state;
  let changed = false;
  const contests = new Map(state.contests);
  for (const c of collisions) {
    if (contests.get(c.linkId)?.key === c.key) continue;
    contests.set(c.linkId, c);
    changed = true;
  }
  return changed ? { ...state, contests } : state;
}

/** Record a choice. Choosing the same answer twice is a no-op. */
export function withResolution(
  state: AmbientRecoveries,
  key: string,
  resolution: AmbientNarrativeContestResolution,
): AmbientRecoveries {
  if (state.resolutions.get(key) === resolution) return state;
  const resolutions = new Map(state.resolutions);
  resolutions.set(key, resolution);
  return { ...state, resolutions };
}

/**
 * Track the lines that just landed. The caller passes exactly what `landingEntries` returned, so a
 * line already tracked (or already in the sink) is never tracked twice.
 */
export function withLanded(state: AmbientRecoveries, landed: readonly OrphanEntry[]): AmbientRecoveries {
  const known = new Set(state.notes.map((n) => n.id));
  const added: TrackedNote[] = [];
  for (const e of landed) {
    const id = noteId(e.text);
    if (known.has(id)) continue;
    known.add(id);
    added.push({ id, text: e.text.trim(), reason: e.reason, status: 'pending' });
  }
  return added.length === 0 ? state : { ...state, notes: [...state.notes, ...added] };
}

/**
 * A save RESOLVED carrying these answers. Every pending line the saved sink actually contains is now
 * `written` — and only those: a line appended after the save was sent is still pending, because the
 * status must not claim a durability the save has not given it.
 */
export function withPersisted(state: AmbientRecoveries, savedAnswers: readonly QRAnswerItem[]): AmbientRecoveries {
  const saved = readSink(savedAnswers) ?? '';
  let changed = false;
  const notes = state.notes.map((n) => {
    if (n.status !== 'pending' || !saved.includes(landedLine(n.text))) return n;
    changed = true;
    return { ...n, status: 'written' as const };
  });
  return changed ? { ...state, notes } : state;
}

/** The clinician withdrew a line. The host has appended the reversal; this records it. */
export function withUndone(state: AmbientRecoveries, id: string): AmbientRecoveries {
  let changed = false;
  const notes = state.notes.map((n) => {
    if (n.id !== id || n.status === 'undone') return n;
    changed = true;
    return { ...n, status: 'undone' as const };
  });
  return changed ? { ...state, notes } : state;
}

/** Look a contest up by the key the DS hands back. */
export function contestByKey(state: AmbientRecoveries, key: string): NarrativeCollision | undefined {
  for (const c of state.contests.values()) if (c.key === key) return c;
  return undefined;
}

/**
 * The DS view of the contests. `locked` (a signed encounter) withholds the choices but KEEPS the
 * pair: the disagreement is still worth reading.
 */
export function toContestViews(state: AmbientRecoveries, locked: boolean): AmbientNarrativeContest[] {
  return [...state.contests.values()].map((c) => {
    const resolution = state.resolutions.get(c.key);
    return {
      id: c.key,
      label: NARRATIVE_SLOT_LABEL[c.slot],
      existing: c.existing,
      dictated: c.dictated,
      ...(resolution ? { resolution } : {}),
      ...(locked ? { actionable: false } : {}),
    };
  });
}

/** The DS view of the recovered lines. `locked` withholds Undo. */
export function toNoteViews(state: AmbientRecoveries, locked: boolean): AmbientRecoveredNote[] {
  return state.notes.map((n) => ({
    id: n.id,
    text: n.text,
    reason: ORPHAN_REASON_LABEL[n.reason],
    status: n.status,
    ...(locked ? { undoable: false } : {}),
  }));
}

function noteId(text: string): string {
  return `recovered::${text.trim()}`;
}
